/**
 * TeachingsCompletedCard Component
 * Card variant for completed rituals in the Teaching tab.
 * Matches the Figma design: image + glass overlay (checkmark, title, tags, Details)
 * + stats bar at the bottom (Views, Joined, Saved, Earnings).
 */

import {
  Banknote,
  Check,
  EllipsisVertical,
  Eye,
  MessageCircleMore,
  Pencil,
  Trash2,
  Upload,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { BookmarkIcon } from "@/components/icons"
import { ShareModal } from "@/components/shared/ShareModal"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useCreateRitualGroupChat,
  useOrganizerChatByRitualId,
} from "@/features/chats/hooks/useRitualGroupChat"
import { formatRitualType } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { cn } from "@/lib"

interface TeachingsCompletedCardProps {
  id: string
  slug?: string
  title: string
  type: string
  participationFee: "Free" | "Paid" | "Donation-based"
  duration: number
  image?: string
  price?: number | string | null
  meetingLink?: string | null
  viewCount?: number
  joinedCount?: number
  savedCount?: number
  earnings?: number
  minimumParticipants?: number | null
  className?: string
  onEditClick?: () => void
  onDeleteClick?: () => void
}

function formatCount(value: number | undefined): string {
  if (value === undefined || value === 0) return "0"
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
  return value.toString()
}

function formatEarnings(value: number | undefined): string {
  if (value === undefined || value === 0) return "$0"
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`
  return `$${value}`
}

function formatPrice(price: number | string | null | undefined): string {
  if (price === undefined || price === null || Number(price) <= 0) return ""
  return `$${Number(price).toFixed(0)}`
}

export function TeachingsCompletedCard({
  id,
  slug,
  title,
  type,
  participationFee,
  duration,
  image,
  price,
  meetingLink,
  viewCount,
  joinedCount,
  savedCount,
  earnings,
  minimumParticipants,
  className,
  onEditClick,
  onDeleteClick,
}: TeachingsCompletedCardProps) {
  const navigate = useNavigate()
  const { data: existingGroupChat } = useOrganizerChatByRitualId(id)
  const createGroupChat = useCreateRitualGroupChat()

  const [shareOpen, setShareOpen] = useState(false)

  const handleChatWithOrganizers = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (existingGroupChat?.id) {
      navigate(`/chats/ritual/${existingGroupChat.id}`)
      return
    }
    try {
      const chat = await createGroupChat.mutateAsync(id)
      navigate(`/chats/ritual/${chat.id}`)
    } catch {
      // error handled by React Query
    }
  }

  const displayPrice = formatPrice(price)
  const typeLabel = formatRitualType(type)
  const minPartLabel =
    minimumParticipants !== undefined && minimumParticipants !== null
      ? `/${minimumParticipants}`
      : ""

  return (
    <>
      <div
        className={cn(
          "relative overflow-hidden rounded-tl-[40px] rounded-br-[40px] h-[339px] md:h-[400px] cursor-pointer flex flex-col",
          className,
        )}
        onClick={() => navigate(`/rituals/${id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            navigate(`/rituals/${id}`)
          }
        }}
      >
        {/* Image background */}
        {image ? (
          <img
            src={image}
            alt={title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-[289px] md:h-[340px] w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 h-[289px] md:h-[340px] bg-card" />
        )}

        <div className="absolute top-5 right-5 flex gap-3 z-20">
          <div
            className="p-3 rounded-lg border-gradient-glass cursor-pointer"
            onClick={handleChatWithOrganizers}
          >
            <MessageCircleMore className="w-5 h-5 text-white" />
          </div>

          <button
            type="button"
            className="p-3 rounded-lg border-gradient-glass cursor-pointer"
            onClick={e => {
              e.stopPropagation()
              setShareOpen(true)
            }}
            aria-label="Share"
          >
            <Upload className="w-5 h-5 text-white" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={e => e.stopPropagation()}
                className="p-3 rounded-lg border-gradient-glass cursor-pointer"
                aria-label="More options"
              >
                <EllipsisVertical className="w-5 h-5 text-white" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white/50 backdrop-blur-sm border-white/20"
            >
              {onEditClick && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    onEditClick()
                  }}
                  className="text-[#000] text-[13px] hover:bg-white/10 cursor-pointer focus:bg-white/10"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {onDeleteClick && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    onDeleteClick()
                  }}
                  className="text-red-400 hover:bg-red-400/10 cursor-pointer focus:bg-red-400/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Glass overlay covering the image area */}
        <div className="absolute top-0 left-0 right-0 h-[289px] md:h-[340px] bg-[rgba(0,0,0,0.75)] flex flex-col items-center justify-center pt-10 px-8">
          {/* Glass checkmark button — top center */}
          <div className="w-[50px] h-[50px] md:w-[70px] md:h-[70px] rounded-full border border-gradient-glass flex items-center justify-center flex-shrink-0 mb-3 md:mb-6">
            <Check className="w-[38px] h-[38px] text-white" strokeWidth={2} />
          </div>

          {/* Center: title + tags */}
          <div className="flex flex-col items-center gap-3 text-center">
            <h3 className="text-sm md:text-[18px] font-semibold text-white leading-[22px] max-w-[500px] md:mb-[11px]">
              {title}
            </h3>

            {/* Tags row */}
            <div className="flex items-center gap-2 flex-wrap justify-center mb-3 md:mb-5">
              {displayPrice && (
                <span className="text-[13px] text-white bg-brand-terracotta-bright rounded px-1.5 py-0.5">
                  {displayPrice}
                </span>
              )}
              {participationFee && participationFee === "Free" && (
                <span className="text-[13px] text-white bg-brand-olive px-1 py-1 rounded-sm">
                  {participationFee}
                </span>
              )}
              {participationFee && participationFee === "Donation-based" && (
                <span className="text-[13px] text-white bg-brand-green px-1 py-1 rounded-sm">
                  {participationFee}
                </span>
              )}
              <span className="text-[13px] text-white">{typeLabel}</span>
              <span className="text-[13px] text-white">{formatDuration(duration)}</span>
              {meetingLink && <span className="text-[13px] text-white">@ Link</span>}
            </div>
          </div>

          {/* Reflect button */}
          <Button
            variant="outline"
            className="h-[50px] w-[180px] rounded-full uppercase font-semibold text-sm flex-shrink-0"
            onClick={e => {
              e.stopPropagation()
              navigate(`/rituals/${id}`)
            }}
          >
            Details
          </Button>
        </div>

        {/* Stats bar at the bottom */}
        <div className="mt-auto h-[61px] bg-white/[0.17] hidden md:flex items-center justify-between px-8 flex-shrink-0">
          <div className="flex items-center gap-6">
            <span className="text-[16px] font-medium text-white/[0.6]">
              Views · <span className="font-semibold">{formatCount(viewCount)}</span>
            </span>
            <span className="text-[16px] font-medium text-white/[0.6]">
              Joined · <span className="font-semibold">{formatCount(joinedCount)}</span>
              {minPartLabel && <span className="opacity-70">{minPartLabel}</span>}
            </span>
            <span className="text-[16px] font-medium text-white/[0.6]">
              Saved · <span className="font-semibold">{formatCount(savedCount)}</span>
            </span>
            {participationFee !== "Free" && (
              <span className="text-[16px] font-medium text-white/[0.6]">
                Earnings · <span className="font-semibold">{formatEarnings(earnings)}</span>
              </span>
            )}
          </div>
        </div>

        <div className="mt-auto h-[50px] bg-white/[0.17] flex md:hidden items-center justify-between px-3.75 flex-shrink-0">
          <div className="flex items-center gap-7.25">
            <span className="text-sm text-white/[0.6] flex item-center gap-2.5">
              <Eye className="w-5 h-5" />{" "}
              <span className="text-primary">{formatCount(viewCount)}</span>
            </span>
            <span className="text-sm text-white/[0.6] flex item-center gap-2.5">
              <Users className="w-5 h-5" />{" "}
              <span>
                <span className="text-primary">{formatCount(joinedCount)}</span>
                {minPartLabel && <span className="opacity-70">{minPartLabel}</span>}
              </span>
            </span>
            <span className="text-sm text-white/[0.6] flex item-center gap-2.5">
              <BookmarkIcon className="w-5 h-5" />{" "}
              <span className="text-primary">{formatCount(savedCount)}</span>
            </span>
            {participationFee !== "Free" && (
              <span className="text-xs text-white/[0.6] flex item-center gap-2.5">
                <Banknote className="w-5 h-5" />{" "}
                <span className="text-primary flex items-center">{formatEarnings(earnings)}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/rituals/${slug || id}`}
        shareText={`Check out this ritual: ${title}`}
        shareType="ritual"
        displayPath={`/rituals/${title.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </>
  )
}
